import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { Utilities } from '../class/utilities';
import { HttpErrorResponse } from '@angular/common/http';
import { MkgError } from '../class/mkg-error';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { UserUtilities } from '@app/class/user-utilities';
import { environment } from '@env/environment';

interface MkgLog {
  timestamp: number;
  caller: string;
  info?: string;
  text?: string;
  error?: string;
}

interface LogSerie {
  id: number;
  companyId: string,
  companyCNPJ: string,
  loggedUserId: string,
  loggedUserEmail: string,
  userRoleInCompany: number,
  SO: string,
  browser: string,
  envName: string,
  companyState: number,
  logs: MkgLog[]
}

export enum LOG_REPORTER_KEYS {
  CASHIER = "cashier-logs",
  STOCK = "stock-logs",
  INVOICE = "mkg-logs",
  UNKNOWN = "unknown",
}

type Series = Array<LogSerie>;

@Injectable({
  providedIn: 'root'
})
export class LogReporterService {
  private _currentId: number;
  private _currentSeriesMap = new Map<LOG_REPORTER_KEYS, LogSerie>();
  readonly DAYS_OF_REPORT = 5; // after x days, the most old reports will be replaced by new reports

  constructor(
    private dataService: DataService
  ) {
    this._currentId = Date.now();
    dataService.ready$.subscribe(([user, company]) => {
      for (const key of [LOG_REPORTER_KEYS.CASHIER, LOG_REPORTER_KEYS.INVOICE, LOG_REPORTER_KEYS.STOCK]) {
        const serie = this._currentSeriesMap.get(key);
        let logs = serie?.logs || [];
        if (serie && serie.companyCNPJ !== company.cnpj) {
          logs.push({
            caller: "LogReporterService",
            timestamp: Date.now(),
            info: `Mudou de "${serie.companyCNPJ}" para "${company.cnpj}"`
          })
        }
        this._currentSeriesMap.set(key, {
          id: this._currentId,
          SO: UserUtilities.getInfosSO(),
          browser: UserUtilities.getInfosBrowser(),
          envName: environment.envName,
          companyState: company.currentState,
          companyId: company.id,
          companyCNPJ: company.cnpj,
          loggedUserEmail: user.email,
          loggedUserId: user.id,
          userRoleInCompany: user.getRoleIn(company),
          logs
        });
      }
    });

    globalThis['downloadReport'] = this.downloadReport.bind(this);
    globalThis['clearReport'] = this.clearReport.bind(this);
  }

  public async downloadReport(all: boolean, key?: LOG_REPORTER_KEYS) {
    const now = moment().format('DD-MM-YYYY');
    const company = this.dataService.company.fancyName.replace(/\./g, '');
    try {
      if (all) {
        const zip = new JSZip();
        Object.values(LOG_REPORTER_KEYS).forEach(logKey => {
          const content = localStorage.getItem(logKey);
          if (content) {
            zip.file(`${logKey}_${company}_${now}.json`, content);
          }
        });

        const blob = await zip.generateAsync({ type: 'blob' });
        saveAs(blob, `LOGS_${company}.zip`);

      } else if (key) {
        const content = localStorage.getItem(key);
        const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${key}_${company}_${now}.json`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }

    } catch (error) {
      console.error("Erro ao baixar relatório", error);
    }
  }

  public clearReport(key: LOG_REPORTER_KEYS){
    localStorage.removeItem(key);
  }

  errorLogger(caller: string, key: LOG_REPORTER_KEYS) {
    return (error: MkgError | any) => {
      try {
        const log: MkgLog = {
          caller,
          timestamp: Date.now(),
          error: JSON.stringify({
            stack: error.stack,
            class: error.constructor?.name,
            cause: error.cause,
            name: (error as HttpErrorResponse).name,
            message: (error as HttpErrorResponse).message,
            error: (error as HttpErrorResponse).error,
            status: (error as HttpErrorResponse).status,
            statusText: (error as HttpErrorResponse).statusText,
            type: (error as HttpErrorResponse).type,
            ok: (error as HttpErrorResponse).ok,
            url: (error as HttpErrorResponse).url,
            body: (error as MkgError).body,
            method: (error as MkgError).method,
            headers: (error as MkgError).rawHeaders,
            attemps: (error as MkgError).attempts,
            reason: error?.reason,
          })
        }
        this._appendLog(log, key)
      } catch (error) {
        console.error("Erro ao adicionar log de erro ao relatório", error)
      }
    }
  }

  textLogger(caller: string, key: LOG_REPORTER_KEYS) {
    return (text: string) => {
      try {
        const log: MkgLog = {
          caller,
          text,
          timestamp: Date.now()
        }
        this._appendLog(log, key)
      } catch (error) {
        console.error("Erro ao adicionar log de texto ao relatório", error)
      }
    }
  }

  private _appendLog(log: MkgLog, key: LOG_REPORTER_KEYS) {
    if(this._currentSeriesMap.has(key)){
      const serie = this._currentSeriesMap.get(key)
      serie.logs.push(log);
      this._currentSeriesMap.set(key, serie);
      this._saveCurrentSerie(key);
    } else {
      console.log(log);
    }
  }

  private _saveCurrentSerie(key: LOG_REPORTER_KEYS) {
    const allSeries = JSON.parse(localStorage.getItem(key) || "[]") as Series;
    const index = allSeries.findIndex(s => s.id === this._currentId);
    if (index != -1) {
      allSeries[index] = this._currentSeriesMap.get(key);
    } else {
      allSeries.push(this._currentSeriesMap.get(key));
    }

    const minDateStamp = Date.now() - (this.DAYS_OF_REPORT * Utilities.MS_DAY)
    const newSeries = allSeries.filter(s => s.id > minDateStamp)

    localStorage.setItem(key, JSON.stringify(newSeries));
  }

}
